(ns app.date
  "The public API for the date utilities"
  (:require #?(:clj  [app.date-impl]
               :cljs [app.front.date-impl])
            #?(:clj [app.time :as time])
            #?(:cljs [core.front.date-util :as date-util])
            [app.date-common :as date]))

(defn date-seq
  "Returns a lazy seq of days from `start` date (inclusive)
  to `end` date (exclusive), where `end` defaults to positive infinity.

  Supports both incrementing seqs (`end` is after `start`) and
  decrementing (`start` is after `end`)."
  ([start] (date/date-seq-impl start))
  ([start end] (date/date-seq-impl start end)))

(defn adjust-date
  "Adjust the given date by the given amount of days.

  Positive and negative `num-of-days` will increment or decrement the date respectively.

  An optional predicate `keep-pred` may be supplied to choose which days to keep and which to skip."
  ([start-date num-of-days]
   (adjust-date start-date num-of-days (constantly true)))
  ([start-date num-of-days keep-pred]
   (let [days-to-keep (fn [d]
                        ;; Handle the case where the start date does NOT satisfy the predicate
                        (or (= start-date d)
                            (keep-pred d)))]
     (cond
       (zero? num-of-days)
       start-date

       (neg? num-of-days)
       (->> (date/make-decrementing-seq start-date)
            (filter days-to-keep)
            (drop (- num-of-days))
            first)

       :else
       (->> (date/make-incrementing-seq start-date)
            (filter days-to-keep)
            (drop num-of-days)
            first)))))

(defn count-days
  "Count the number of days between `start-date` (inclusive)
  and `end-date` (exclusive).

  If `start-date` is before `end-date`, the counting happens in reverse
  and the result will be negative.

  An optional predicate `pred` may be supplied to choose which days to count."
  ([start-date end-date]
   (count-days start-date end-date (constantly true)))
  ([start-date end-date pred]
   (let [[start end negate?] (if (date/is-before? start-date end-date)
                               [start-date end-date false]
                               ;; Swap days and increment to maintain symmetry
                               [(adjust-date end-date 1)
                                (adjust-date start-date 1)
                                true])
         days                (->> (date-seq start end)
                                  (filter pred)
                                  count)]
     (if negate? (- days) days))))

#_{:clj-kondo/ignore #?(:clj [:unused-binding] :cljs [])}
(defn goog-date [date]
  #?(:cljs (goog.date.DateTime.
             (.getFullYear date)
             (.getMonth date)
             (.getDate date)
             (.getHours date)
             (.getMinutes date)
             (.getSeconds date)
             (.getMilliseconds date))))

(defn local-date
  "Return the calendar date represented by `date` in `timezone`."
  [date timezone]
  #?(:clj  (-> date (time/in-zone timezone) .toLocalDate)
     :cljs (let [local-date (date-util/to-tz date timezone)]
             (goog.date.Date. (.getFullYear local-date)
                              (.getMonth local-date)
                              (.getDate local-date)))))

(defn year-in-tz
  "Return the calendar year represented by `date` in `timezone`."
  [date timezone]
  #?(:clj  (-> date (time/in-zone timezone) .getYear)
     :cljs (-> date (date-util/to-tz timezone) .getFullYear)))

(defn local-day-marker
  "Represent the local date at noon so it falls within full-day blackout boundaries."
  [date timezone]
  #?(:clj  (-> date
               (time/start-of-day-in-tz timezone)
               (time/add-time-to-date (* 12 60 60)))
     :cljs (-> date
               (date-util/to-tz timezone)
               (date-util/add-minutes-to-date (* 12 60)))))

(defn previous-local-day-marker [date timezone]
  #?(:clj  (-> date
               (local-day-marker timezone)
               (time/in-zone timezone)
               (time/add-days-to-zdt -1)
               time/zdt->date)
     :cljs (-> date
               (local-day-marker timezone)
               goog-date
               (adjust-date -1)
               js/Date.)))

;; Coercers for transforming dates and datetimes

#_{:clj-kondo/ignore #?(:cljs [:unused-binding :redundant-fn-wrapper] :clj [])}
(defn coerce-timestamp-fn
  "Return a function for coercing external timestamps (`j.u.Date`s, `js/Date`s)
  into datetime objects with richer APIs"
  [{:keys [timezone]}]
  #?(:clj  (fn [date] (time/in-zone date timezone))
     :cljs (fn [date] (goog-date date))))

(defn uncoerce-timestamp-fn
  []
  #?(:clj  time/zdt->date
     :cljs (fn [date] (js/Date. date))))

#_{:clj-kondo/ignore #?(:cljs [:unused-binding] :clj [])}
(defn clamp-date-range
  [start end limit tz]
  #?(:clj  (let [start-zdt  (time/in-zone start tz)
                 days-count (count-days start-zdt (time/in-zone end tz))
                 end-date   (if (> days-count limit)
                              (-> (adjust-date start-zdt limit)
                                  time/zdt->date)
                              end)]
             [start end-date])))
